'use client';

import { motion, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiArrowDownLine, RiShieldKeyholeLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { AuthAsideRays } from '@/components/auth-aside-rays';
import { dur, ease, mockContainer, mockItem, spring, springFade, stagger } from '@/lib/motion';

/**
 * Giriş ekranının koyu yarısı — "siz yokken ne oldu" paneli.
 *
 * Neden bu içerik: giriş ekranı bir eşik. Kullanıcı burada bir şey okumaya
 * gelmiyor, girmeye geliyor — o yüzden sağ taraf ürün pazarlaması yapmıyor,
 * ürünün İŞİNİ gösteriyor. Panel, bu hesapta gece boyunca çalışan ajanların
 * çıktısı gibi duruyor: bir ölçüm, bir eğilim, üç olay satırı. Giriş yaptıktan
 * sonra göreceği ekranın küçültülmüş hâli.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Çubuklar TABANDAN büyüyor (`scaleY`, origin bottom): ölçüm sıfırdan
 * başlıyor, havada belirmiyor. Soldan sağa 0.04 sn arayla — zaman ekseni
 * soldan sağa akıyor, çubuklar da o yönde "yazılıyor". Sonuncu çubuk yeşil ve
 * en son geliyor: son ölçüm, yani şu an.
 *
 * Olay satırları çubuklardan SONRA giriyor — önce sonuç (maliyet düştü), sonra
 * gerekçesi (hangi ajan ne yaptı). Ters sırada okunsaydı satırlar bağlamsız
 * kalırdı.
 *
 * 🔑 Canlı noktanın nabzı sonsuz döngü: `useReducedMotion()` ile elle
 * kapatılıyor (DESIGN.md §1). "Canlı" bilgisini nokta tek başına taşımıyor,
 * yanındaki etiket de söylüyor.
 *
 * ⚠️ Panel rakamları ÖRNEK. Gerçek veri değil ve gerçekmiş gibi de
 * sunulmuyor: kartın köşesinde "örnek panel" etiketi var. Bu ekran canlı
 * veriye bağlanacaksa etiket kalkar; bağlanmayacaksa etiket KALIR.
 */

/**
 * Dönüşüm başına maliyetin 20 günlük seyri — yüzde olarak, en yüksek gün 100.
 * Düşen bir eğri; aradaki yükselişler bilinçli: kusursuz inen bir çizgi grafik
 * değil, süs gibi duruyor.
 *
 * Neden 20 ve 12 değil: kartın içinde çubuk başına ~14px kalıyor. 12 çubukta
 * bu 26px oluyordu — neredeyse kare bloklar, grafikten çok tuğla dizisi.
 * İnce çubuk "ölçüm" okutuyor, kalın çubuk "kutu".
 */
const BARS = [100, 96, 99, 91, 88, 92, 84, 80, 83, 75, 71, 74, 66, 62, 64, 56, 51, 53, 44, 36];

const EVENTS = ['budget', 'pause', 'creative'] as const;

const asideV: Variants = {
  hidden: {},
  /* Sol sütun bir tık önce başlıyor: göz önce forma düşsün, panel arkadan
     gelsin. Aynı anda başlayan iki sütun dikkati ikiye bölüyordu. */
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.18 } },
};

const asideItem: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Kart tek parça geliyor, içi ayrıca sıralanıyor (mockContainer). */
const cardV: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.985 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { ...springFade(spring.soft), staggerChildren: stagger.tight, delayChildren: 0.1 },
  },
};

const barsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const barV: Variants = {
  hidden: { scaleY: 0 },
  show: { scaleY: 1, transition: { duration: dur.base, ease: ease.out } },
};

export function LoginAside() {
  const t = useTranslations('login.aside');
  const reduceMotion = useReducedMotion();

  return (
    /* aria-hidden DEĞİL: içerik gerçek bilgi taşıyor ve ekran okuyucu
       kullanıcısı da "sistem çalışıyor" mesajını hak ediyor. Ama formdan
       SONRA okunsun diye DOM'da sonra duruyor — görsel sıra da aynı. */
    <aside className="auth-aside">
      <AuthAsideRays />

      <motion.div
        className="auth-aside-inner"
        variants={asideV}
        initial="hidden"
        animate="show"
      >
        <motion.p className="auth-aside-eyebrow" variants={asideItem}>
          <span className="auth-live" aria-hidden="true">
            {/* Halka HER ZAMAN basılıyor, azaltılmış harekette de. Koşullu
                render (`{!reduceMotion && …}`) hidrasyonu kırıyordu: sunucu
                `useReducedMotion()`'ı false okuyup halkayı basıyor, istemci
                tercihi açık bulup basmıyor, React ağacı uyuşmuyor ve konsola
                "Hydration failed" düşüyordu.

                Durdurma artık `transition` üzerinden — o hiç HTML üretmiyor,
                yani iki tarafın işaretlemesi birebir aynı. duration 0 ile
                halka son kareye (scale 2.4, opacity 0) anında gidiyor: görünmez
                ve döngü yok. */}
            <motion.span
              className="auth-live-ring"
              animate={{ scale: [1, 1.3, 2.4], opacity: [0, 0.5, 0] }}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { duration: 2.4, repeat: Infinity, ease: 'easeOut', times: [0, 0.16, 1] }
              }
            />
          </span>
          {t('live')}
        </motion.p>

        <motion.h2 className="auth-aside-title" variants={asideItem}>
          {t('title')}
        </motion.h2>

        <motion.p className="auth-aside-lead" variants={asideItem}>
          {t('lead')}
        </motion.p>

        <motion.div className="auth-card" variants={cardV}>
          {/* Köşe işaretleri — hero'nun .rectangles-outside-* diliyle aynı.
              Kartı "ölçülmüş" gösteriyorlar; süs değil, sayfanın ızgarasının
              bu kartta da devam ettiğinin işareti. */}
          <span className="auth-card-mark auth-card-mark--tl" aria-hidden="true" />
          <span className="auth-card-mark auth-card-mark--tr" aria-hidden="true" />
          <span className="auth-card-mark auth-card-mark--bl" aria-hidden="true" />
          <span className="auth-card-mark auth-card-mark--br" aria-hidden="true" />

          <motion.div className="auth-card-head" variants={mockItem}>
            <span className="auth-card-label">{t('metric.label')}</span>
            <span className="auth-card-tag">{t('demo')}</span>
          </motion.div>

          <motion.div className="auth-card-metric" variants={mockItem}>
            <span className="auth-card-value">{t('metric.value')}</span>
            <span className="auth-card-delta">
              <RiArrowDownLine size={14} aria-hidden="true" />
              {t('metric.delta')}
            </span>
          </motion.div>

          <motion.div className="auth-bars" variants={barsV} aria-hidden="true">
            {BARS.map((value, i) => (
              <motion.span
                key={value + '-' + i}
                className={`auth-bar${i === BARS.length - 1 ? ' auth-bar--now' : ''}`}
                style={{ height: `${value}%` }}
                variants={barV}
              />
            ))}
          </motion.div>

          <motion.p className="auth-card-caption" variants={mockItem}>
            {t('metric.caption')}
          </motion.p>

          <motion.ul className="auth-events" variants={mockContainer}>
            {EVENTS.map((key) => (
              <motion.li key={key} className="auth-event" variants={mockItem}>
                <span className="auth-event-time">{t(`events.${key}.time`)}</span>
                <span className="auth-event-tag">{t(`events.${key}.platform`)}</span>
                <span className="auth-event-text">{t(`events.${key}.text`)}</span>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.p className="auth-aside-note" variants={asideItem}>
          <RiShieldKeyholeLine size={16} aria-hidden="true" />
          {t('note')}
        </motion.p>
      </motion.div>
    </aside>
  );
}
